import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import RelatedPostsInline from '../components/RelatedPostsInline';
import { regions as mockRegions } from '../data/mock';
import { getRegions } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { usePageSEO } from '../hooks/usePageSEO';
import { buildWhatsAppUrl } from '../utils/whatsapp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  pt: {
    eyebrow: 'Geografias da curadoria',
    heroTitle: 'Portugal não é um único mercado.',
    heroTitleEm: 'São vários, em sintonia.',
    heroLead:
      'Cada região do nosso mandato tem temperamento próprio — luz, ritmo, comunidade, fiscalidade. Conhecemo-las uma a uma. Apresentamos cada uma como ela é.',

    portugalEyebrow: 'Portugal como estilo de vida',
    portugalTitle: 'Quatro razões que persistem.',
    portugalSub:
      'Para além do dossier de oportunidades, há razões estruturais para Portugal estar entre os destinos mais consultados pelo comprador internacional.',
    portugalValues: [
      { title: 'Clima', body: 'Mais de 300 dias de sol por ano na costa atlântica. Invernos amenos, verões ventilados pela proximidade do oceano.' },
      { title: 'Segurança', body: 'Entre os países mais seguros do mundo. Estabilidade política, sistema judicial funcional, baixa criminalidade.' },
      { title: 'Cultura', body: 'Património UNESCO em cada região. Gastronomia, vinho, arquitetura. Hospitalidade discreta e genuína.' },
      { title: 'Fiscalidade', body: 'Regime fiscal favorável para novos residentes (NHR sucessor), tratados de dupla tributação com mais de 80 países.' },
    ],

    regionsEyebrow: 'As nossas regiões',
    regionsCta: 'Explorar',

    closingTitle: 'Cada região começa por uma conversa.',
    closingBody:
      'Apresentamos cada geografia como ela é — não como vende. Se quer compreender qual região se adequa ao seu projeto, começamos por uma conversa privada.',
    closingCta: 'Iniciar conversa',
  },
  en: {
    eyebrow: 'Geographies of curation',
    heroTitle: 'Portugal is not a single market.',
    heroTitleEm: 'It is several, in harmony.',
    heroLead:
      'Each region of our mandate has its own temperament — light, rhythm, community, taxation. We know them one by one. We present each as it truly is.',

    portugalEyebrow: 'Portugal as a way of living',
    portugalTitle: 'Four reasons that endure.',
    portugalSub:
      'Beyond the dossier of opportunities, there are structural reasons why Portugal sits among the most-consulted destinations for the international buyer.',
    portugalValues: [
      { title: 'Climate', body: 'More than 300 days of sun a year on the Atlantic coast. Mild winters, summers ventilated by ocean proximity.' },
      { title: 'Safety', body: 'Among the safest countries in the world. Political stability, functional judicial system, low criminality.' },
      { title: 'Culture', body: 'UNESCO heritage in every region. Gastronomy, wine, architecture. Discreet and genuine hospitality.' },
      { title: 'Taxation', body: 'Favourable tax regime for new residents (successor NHR), double taxation treaties with over 80 countries.' },
    ],

    regionsEyebrow: 'Our regions',
    regionsCta: 'Explore',

    closingTitle: 'Each region begins with a conversation.',
    closingBody:
      'We present each geography as it is — not as it sells. If you wish to understand which region fits your project, we begin with a private conversation.',
    closingCta: 'Begin a conversation',
  },
};

const PORTUGAL_IMAGE = 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?auto=format&fit=crop&w=1800&q=85';

// Region fallback hero images (used when API region has no image)
const REGION_FALLBACK = {
  lisboa: 'https://images.unsplash.com/photo-1588001400947-6385aef4ab0e?auto=format&fit=crop&w=1600&q=85',
  cascais: 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?auto=format&fit=crop&w=1600&q=85',
  comporta: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1600&q=85',
  algarve: 'https://images.unsplash.com/photo-1537254489012-e2c5ee6b8df6?auto=format&fit=crop&w=1600&q=85',
  porto: 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?auto=format&fit=crop&w=1600&q=85',
  sintra: 'https://images.unsplash.com/photo-1513415564515-763d91423bdd?auto=format&fit=crop&w=1600&q=85',
};

// Discreet curatorial tags per region — three single-word descriptors that
// capture the character of each geography. Slug-keyed so the mapping holds
// regardless of how the region is named in the CMS (PT or EN).
const REGION_TAGS = {
  pt: {
    lisboa: ['Capital', 'Cultura', 'Hospitalidade'],
    cascais: ['Costa', 'Internacional', 'Family-friendly'],
    comporta: ['Atlântico', 'Pinhal', 'Discrição'],
    algarve: ['Mediterrânico', 'Golfe', 'Investimento'],
    porto: ['Património', 'Vinho', 'Renovação'],
    sintra: ['Heritage', 'Romântico', 'Montanha'],
    costa: ['Atlântico', 'Surf', 'Natureza'],
  },
  en: {
    lisboa: ['Capital', 'Culture', 'Hospitality'],
    cascais: ['Coast', 'International', 'Family-friendly'],
    comporta: ['Atlantic', 'Pine forest', 'Discretion'],
    algarve: ['Mediterranean', 'Golf', 'Investment'],
    porto: ['Heritage', 'Wine', 'Renovation'],
    sintra: ['Heritage', 'Romantic', 'Mountains'],
    costa: ['Atlantic', 'Surf', 'Nature'],
  },
};

const getRegionSlug = (regionName) => {
  const normalized = (regionName || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  let slug = '';
  for (const ch of normalized) slug += /[a-z0-9]/.test(ch) ? ch : '-';
  return slug.replace(/^-+|-+$/g, '').replace(/--+/g, '--');
};

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (e.target.classList.add('is-visible'), io.unobserve(e.target))),
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );
    el.querySelectorAll('.rv').forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);
  return ref;
};

const Eyebrow = ({ children, dark = false }) => (
  <p
    className={`inline-flex items-center gap-3 text-[11px] font-medium uppercase ${
      dark ? 'text-editorial-bronze-lt' : 'text-editorial-bronze'
    }`}
    style={{ letterSpacing: '0.24em', fontFamily: 'DM Sans, sans-serif' }}
  >
    <span className={`block w-7 h-px ${dark ? 'bg-editorial-bronze-lt' : 'bg-editorial-bronze'}`} />
    {children}
  </p>
);

const RegioesPage = () => {
  const { language, localizedPath } = useLanguage();
  const copy = COPY[language] || COPY.pt;
  const { title: seoTitle, description: seoDescription } = usePageSEO('regioes', language);
  const ref = useReveal();
  const [homeContent, setHomeContent] = useState(null);
  const [regions, setRegions] = useState(mockRegions);

  const getTranslated = (obj, field) => {
    if (!obj) return '';
    if (language === 'en' && obj[`${field}_en`]) return obj[`${field}_en`];
    return obj[field] || '';
  };

  useEffect(() => {
    (async () => {
      try {
        const [home, reg] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/home-content`),
          getRegions(),
        ]);
        setHomeContent(home.data);
        if (reg && reg.length > 0) setRegions(reg);
      } catch (e) {
        // graceful fallback to mock data
      }
    })();
  }, []);

  return (
    <>
      <SEO title={seoTitle} description={seoDescription} url="https://yourbrokerportugal.com/regioes" />
      <style>{`
        body { background: #f7f4ef; }
        .rv { opacity: 0; transform: translateY(28px); transition: opacity .95s ease, transform .95s ease; }
        .rv.is-visible { opacity: 1; transform: translateY(0); }
      `}</style>

      <Header companyName={homeContent?.headerFooter?.companyName} logo={homeContent?.headerFooter?.logo} />

      <main ref={ref}>
        {/* HERO */}
        <section className="pt-28 md:pt-36 pb-16 md:pb-20 px-6 md:px-10 lg:px-16 xl:px-24 bg-editorial-mineral">
          <div className="max-w-5xl mx-auto">
            <div className="rv">
              <Eyebrow>{copy.eyebrow}</Eyebrow>
            </div>
            <h1
              className="rv text-editorial-charcoal font-light mt-7 mb-9 max-w-4xl"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2.4rem, 4.4vw, 4rem)', lineHeight: '1.08' }}
            >
              {copy.heroTitle}{' '}
              <em className="italic text-editorial-bronze">{copy.heroTitleEm}</em>
            </h1>
            <p
              className="rv text-editorial-muted font-light max-w-2xl"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
            >
              {copy.heroLead}
            </p>
          </div>
        </section>

        {/* PORTUGAL AS A LIFESTYLE — split */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28 lg:py-32 bg-editorial-sand/50">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="rv lg:col-span-5">
              <div className="aspect-[4/5] overflow-hidden bg-editorial-sand">
                <img
                  src={PORTUGAL_IMAGE}
                  alt="Portugal coast"
                  className="w-full h-full object-cover"
                  style={{ filter: 'grayscale(0.1) contrast(1.03)' }}
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="rv">
                <Eyebrow>{copy.portugalEyebrow}</Eyebrow>
                <h2
                  className="text-editorial-charcoal font-light mt-7 mb-7"
                  style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.2vw, 3rem)', lineHeight: '1.18' }}
                >
                  {copy.portugalTitle}
                </h2>
                <p
                  className="text-editorial-muted font-light max-w-xl mb-10"
                  style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
                >
                  {copy.portugalSub}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-9">
                {copy.portugalValues.map((v, i) => (
                  <div key={v.title} className="rv">
                    <span
                      className="text-editorial-bronze block mb-3"
                      style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.6rem', letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 500 }}
                    >
                      0{i + 1}
                    </span>
                    <h3
                      className="text-editorial-charcoal font-normal mb-3"
                      style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.45rem', lineHeight: '1.2' }}
                    >
                      {v.title}
                    </h3>
                    <p
                      className="text-editorial-muted font-light"
                      style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.92rem', lineHeight: '1.88' }}
                    >
                      {v.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* REGIONS — editorial split list */}
        <section id="regioes" className="bg-editorial-mineral">
          <div className="px-6 md:px-10 lg:px-16 xl:px-24 pt-20 md:pt-28 pb-8">
            <div className="max-w-7xl mx-auto">
              <div className="rv">
                <Eyebrow>{copy.regionsEyebrow}</Eyebrow>
              </div>
            </div>
          </div>

          {regions.map((region, index) => {
            const slug = getRegionSlug(region.name);
            const isEven = index % 2 === 0;
            const fallback = REGION_FALLBACK[slug.split('-')[0]] || PORTUGAL_IMAGE;
            const img = region.image && !region.image.startsWith('http') ? `${BACKEND_URL}${region.image}` : region.image || fallback;
            const name = getTranslated(region, 'name');
            const description = getTranslated(region, 'description');
            const character = getTranslated(region, 'character') || '';
            const tagKey = slug.split('-')[0];
            const tagsMap = REGION_TAGS[language] || REGION_TAGS.pt;
            // Fallback: try the first segment, otherwise match any known key
            // that appears anywhere inside the slug ("linha-de-cascais" → cascais).
            let tags = tagsMap[tagKey];
            if (!tags) {
              const matchedKey = Object.keys(tagsMap).find((k) => slug.includes(k));
              tags = matchedKey ? tagsMap[matchedKey] : [];
            }

            return (
              <article
                key={region.id || region.name}
                id={slug}
                className={`px-6 md:px-10 lg:px-16 xl:px-24 py-16 md:py-24 ${index < regions.length - 1 ? 'border-b' : ''}`}
                style={{ borderColor: 'rgba(28,28,26,0.08)' }}
                data-testid={`region-${slug}`}
              >
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                  {/* Image */}
                  <div className={`rv lg:col-span-6 ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                    <div className="aspect-[5/4] overflow-hidden bg-editorial-sand">
                      <img
                        src={img}
                        alt={name}
                        className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`lg:col-span-6 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                    <div className="rv">
                      {character && (
                        <p
                          className="text-editorial-bronze mb-4"
                          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.62rem', letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 500 }}
                        >
                          {character.slice(0, 60)}
                        </p>
                      )}
                      <h2
                        className="text-editorial-charcoal font-light mb-6"
                        style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.4vw, 3.2rem)', lineHeight: '1.12' }}
                      >
                        {name}
                      </h2>
                      <p
                        className="text-editorial-muted font-light mb-7 max-w-xl"
                        style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
                      >
                        {description}
                      </p>

                      {tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-8" data-testid={`region-tags-${slug}`}>
                          {tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-block py-1.5 px-3.5 border border-editorial-sand-deep text-editorial-charcoal-soft"
                              style={{
                                fontFamily: 'DM Sans, sans-serif',
                                fontSize: '0.6rem',
                                letterSpacing: '0.18em',
                                textTransform: 'uppercase',
                                fontWeight: 500,
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {region.priceRange && (
                        <div className="inline-flex items-center gap-3 mb-8 pb-3 border-b border-editorial-sand-deep">
                          <span
                            className="text-editorial-bronze"
                            style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.6rem', letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 500 }}
                          >
                            {language === 'en' ? 'Reference' : 'Referência'}
                          </span>
                          <span
                            className="text-editorial-charcoal"
                            style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.05rem', fontWeight: 400 }}
                          >
                            {region.priceRange}
                          </span>
                        </div>
                      )}

                      <div>
                        <Link
                          to={localizedPath(`/contact?regiao=${slug}`)}
                          className="inline-flex items-center text-editorial-charcoal hover:text-editorial-bronze transition-colors border-b border-editorial-charcoal hover:border-editorial-bronze pb-1"
                          style={{
                            fontFamily: 'DM Sans, sans-serif',
                            fontSize: '0.65rem',
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            fontWeight: 500,
                          }}
                          data-testid={`region-cta-${slug}`}
                        >
                          {copy.regionsCta} {name}
                        </Link>
                      </div>

                      <RelatedPostsInline regionSlug={slug} limit={3} />
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </section>

        {/* CLOSING */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-24 md:py-36 bg-editorial-charcoal text-editorial-bone">
          <div className="max-w-4xl mx-auto text-center">
            <div className="rv">
              <h2
                className="text-editorial-bone font-light mb-7"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.6vw, 3.2rem)', lineHeight: '1.12' }}
              >
                {copy.closingTitle}
              </h2>
              <p
                className="text-editorial-bone/55 max-w-2xl mx-auto font-light mb-12"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
              >
                {copy.closingBody}
              </p>
              <div className="flex flex-col sm:flex-row gap-5 sm:items-center sm:justify-center">
                <a
                  href={buildWhatsAppUrl('regions', language)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-editorial-charcoal bg-editorial-sand hover:bg-editorial-bone transition-all duration-300 hover:-translate-y-0.5 py-4 px-10"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.65rem',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                    borderRadius: 0,
                  }}
                  data-testid="regions-closing-cta-whatsapp"
                >
                  {language === 'pt' ? 'Falar via WhatsApp' : 'Chat via WhatsApp'}
                </a>
                <Link
                  to={localizedPath('/contact')}
                  className="text-editorial-bone/60 hover:text-editorial-bone border-b border-editorial-bone/22 hover:border-editorial-bone pb-0.5 transition-colors"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.65rem',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  }}
                  data-testid="regions-closing-cta"
                >
                  {language === 'pt' ? 'Ou enviar pedido privado' : 'Or send a private enquiry'}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer
        data={{
          ...(homeContent?.headerFooter || {}),
          footerDescription:
            language === 'en'
              ? 'Private buying agent for international buyers in Portugal.'
              : 'Consultoria privada para compradores internacionais em Portugal.',
        }}
      />
    </>
  );
};

export default RegioesPage;
